import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, MessageCircle, Calendar, Phone } from 'lucide-react';
import { Button } from './ui/Button';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useTranslation } from '../i18n';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-324">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-325" />
    </svg>;
}
export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {
    t,
    lang
  } = useTranslation();
  const navigation = [{
    name: t('nav.home'),
    href: `/${lang}`
  }, {
    name: t('nav.services'),
    href: `/${lang}/services`
  }, {
    name: t('nav.prices'),
    href: `/${lang}/prices`
  }, {
    name: t('nav.about'),
    href: `/${lang}/about`
  }, {
    name: t('nav.contact'),
    href: `/${lang}/contact`
  }];
  return <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-md border-b border-gray-100" data-id="element-326">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-327">
        <div className="flex justify-between items-center h-20" data-id="element-328">
          {/* Logo */}
          <Link to={`/${lang}`} className="flex items-center gap-2" data-id="element-329">
            <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center text-white font-bold text-xl" data-id="element-330">
              D
            </div>
            <span className="text-xl font-bold text-gray-900 tracking-tight" data-id="element-331">
              Dentico<span className="text-teal-600" data-id="element-332">Clinic</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6" data-id="element-333">
            {navigation.map(item => <Link key={item.href} to={item.href} className="text-sm font-medium text-gray-600 hover:text-teal-600 transition-colors" data-id="element-334">
                {item.name}
              </Link>)}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-4" data-id="element-335">
            <LanguageSwitcher data-id="element-336" />
            <div className="h-6 w-px bg-gray-200" data-id="element-337" />
            <a href="tel:+447345197820" className="text-gray-500 hover:text-teal-600 transition-colors" aria-label="Phone" data-id="element-338">
              <Phone className="w-5 h-5" data-id="element-339" />
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-green-600 transition-colors" aria-label="WhatsApp" data-id="element-340">
              <MessageCircle className="w-5 h-5" data-id="element-341" />
            </a>
            <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-blue-500 transition-colors" aria-label="Telegram" data-id="element-342">
              <TelegramIcon className="w-5 h-5" data-id="element-343" />
            </a>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-344">
              <Button size="sm" className="gap-2" data-id="element-345">
                <Calendar className="w-4 h-4" data-id="element-346" />
                {t('nav.bookOnline')}
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-2 flex-shrink-0" data-id="element-347">
            <LanguageSwitcher data-id="element-348" />
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="relative z-50 flex items-center justify-center min-w-[44px] min-h-[44px] -mr-2 text-gray-700 hover:text-gray-900 active:bg-gray-100 rounded-lg transition-colors" aria-label={isMenuOpen ? 'Close menu' : 'Open menu'} type="button" data-id="element-349">
              {isMenuOpen ? <X className="w-6 h-6" data-id="element-350" /> : <Menu className="w-6 h-6" data-id="element-351" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && <div className="md:hidden border-t border-gray-100 bg-white relative z-50" data-id="element-352">
          <div className="px-4 py-6 space-y-4" data-id="element-353">
            <nav className="flex flex-col space-y-4" data-id="element-354">
              {navigation.map(item => <Link key={item.href} to={item.href} className="text-lg font-medium text-gray-900" onClick={() => setIsMenuOpen(false)} data-id="element-355">
                  {item.name}
                </Link>)}
            </nav>
            <div className="pt-4 border-t border-gray-100 space-y-3" data-id="element-356">
              <a href="tel:+447345197820" className="block" data-id="element-357">
                <Button variant="outline" fullWidth className="gap-2" data-id="element-358">
                  <Phone className="w-4 h-4 text-teal-600" data-id="element-359" />
                  07345 197820
                </Button>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="block" data-id="element-360">
                <Button variant="outline" fullWidth className="gap-2" data-id="element-361">
                  <MessageCircle className="w-4 h-4 text-green-600" data-id="element-362" />
                  WhatsApp
                </Button>
              </a>
              <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" className="block" data-id="element-363">
                <Button variant="outline" fullWidth className="gap-2" data-id="element-364">
                  <TelegramIcon className="w-4 h-4 text-blue-500" data-id="element-365" />
                  Telegram
                </Button>
              </a>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="block" data-id="element-366">
                <Button fullWidth className="gap-2" data-id="element-367">
                  <Calendar className="w-4 h-4" data-id="element-368" />
                  {t('nav.bookOnline')}
                </Button>
              </a>
            </div>
          </div>
        </div>}
    </header>;
}